import React, { memo, useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { useAppDispatch } from "@zenith/app/hooks";
import { CurrencyFieldComponent } from "@zenith/components";
import { usePaymentsContext } from "@zenith/context";
import {
  PRODUCT_PLACEHOLDER_LABEL,
  UNIT_PRICE_LABEL,
} from "@zenith/data/constants";
import { PaymentMethodsEnum } from "@zenith/features/Payments/types";
import { addToCart, selectCart } from "./cartSlice";

/* eslint-disable-next-line */
interface PaymentProps {
  openModal: () => void;
  closeModal: () => void;
}

const ItemsDetailsFormComponent = memo(function ItemsDetailsFormComponent({
  openModal,
  closeModal,
}: PaymentProps) {
  const { paymentsContext } = usePaymentsContext();
  const paymentMethod = paymentsContext.paymentMethod;
  const [amount, setAmount] = useState("");
  const [quantity, setQuantity] = useState<number | null>(null);
  const [description, setDescription] = useState("");
  const [formValid, setFormValid] = useState(false);
  const [apuError, setApuError] = useState("");

  const dispatch = useAppDispatch();
  const cart = useSelector(selectCart);

  const handleAddToCart = (event: React.MouseEvent<HTMLButtonElement>) => {
    const lastID = cart[cart.length - 1]?.id;
    const item = {
      id: lastID ? lastID + 1 : 1,
      amount,
      quantity: quantity ?? 1,
      description: description || PRODUCT_PLACEHOLDER_LABEL,
    };

    event.preventDefault();
    dispatch(addToCart(item));

    setAmount("");
    setQuantity(null);
    setDescription("");
  };

  const handleCheckout = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (
      cart.length === 0 ||
      (cart.length !== 0 && amount !== "" && Number(amount) !== 0)
    ) {
      handleAddToCart(event);
      openModal();
    } else {
      event.preventDefault();
      openModal();
    }
  };

  const validateForm = () => {
    return amount !== "" && Number(amount.replace(/,/g, "")) > 0;
  };

  useEffect(() => {
    setFormValid(validateForm());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amount, quantity, description]);

  const handleAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event) {
      let value = event.target.value;
      setApuError("");

      value = value.replace(/^0+(?=\d)/, "").replace(/^0+(\d)(?=\.\d*)/, "$1");

      if (value.startsWith(".")) {
        value = `0${value}`;
      }

      setAmount(value);
    }
  };

  const handleQuantityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const regex = /^[1-9][0-9]{0,4}$/;

    const cleanedInput = event.target.value.replace(/[^0-9]/g, "");
    let quantityValue: number | null = parseInt(cleanedInput);

    if (!regex.test(cleanedInput)) {
      quantityValue = null;
    }
    if (event.target.value.length > 5) {
      quantityValue = parseInt(event.target.value.slice(0, 5));
    }
    setQuantity(quantityValue);
  };

  const handleDescriptionChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newDescription = event.target.value;
    const isValid = /^[A-Za-z0-9 ]*$/.test(newDescription);

    if (isValid && newDescription.length <= 50) {
      setDescription(newDescription);
    }
  };

  const handleAmountFocus = () => {
    if (amount.trim() === "" && cart.length === 0) {
      setApuError("Amount required");
    } else if (parseFloat(amount) === 0 && cart.length === 0) {
      setApuError("Amount must be greater than zero");
    } else if (amount.trim() === "" && cart.length > 0) {
      setApuError("Amount required to add item(s) to cart");
    } else if (parseFloat(amount) === 0 && cart.length > 0)
      setApuError("Amount must be greater than zero to add item to cart");
    else {
      setApuError("");
    }
  };

  function isPaymentMethodValid() {
    if (paymentMethod === undefined) return false;
    return Object.values(PaymentMethodsEnum).includes(paymentMethod);
  }

  return (
    <div className="flex w-full flex-col self-start p-8 pt-14">
      <h2 className="text-charcoal-black mb-6 text-2xl font-medium leading-relaxed">
        Item Details
      </h2>

      <form id="cart-form" className="flex w-full flex-col">
        <CurrencyFieldComponent
          id="apu-input"
          className="w-full"
          label={UNIT_PRICE_LABEL}
          value={amount}
          errorMessage={apuError}
          hasError={Boolean(apuError)}
          onBlur={handleAmountFocus}
          onChange={handleAmountChange}
        />

        <label className="flex flex-col text-sm font-normal leading-loose text-neutral-500">
          Quantity
          <input
            id="quantity-button"
            type="text"
            min="1"
            value={quantity === null ? "" : quantity}
            placeholder="1"
            className={`mb-2 rounded-lg border border-neutral-200 px-4 py-3 text-lg leading-normal text-black focus:outline-neutral-900`}
            maxLength={5}
            onChange={handleQuantityChange}
          />
        </label>

        <label className="flex flex-col  text-sm font-normal leading-loose text-neutral-500">
          Item Description
          <input
            type="text"
            id="item-description-input"
            value={description}
            placeholder={PRODUCT_PLACEHOLDER_LABEL}
            className={`mb-2 rounded-lg border border-neutral-200 px-4 py-3 text-lg leading-normal text-black focus:outline-neutral-900`}
            maxLength={50}
            onChange={handleDescriptionChange}
          />
        </label>
      </form>

      <div className="mt-16 flex justify-center space-x-4">
        <button
          id="atc-button"
          form="cart-form"
          className={`h-12 flex-1 rounded-lg text-base font-medium ${
            !formValid
              ? "cursor-not-allowed border border-neutral-100 bg-white text-neutral-100"
              : "text-neutral-550 border border-neutral-200  bg-white"
          }`}
          onClick={handleAddToCart}
          disabled={!formValid}
        >
          Add to Ca
        </button>
        <button
          id="checkout-button"
          type="submit"
          form="cart-form"
          onClick={handleCheckout}
          disabled={
            !isPaymentMethodValid() || (!formValid && cart.length === 0)
          }
          className={` h-12 flex-1 rounded-lg text-base font-medium ${
            !isPaymentMethodValid() || (!formValid && cart.length === 0)
              ? "cursor-not-allowed bg-neutral-50 text-neutral-200"
              : "bg-primary-300 text-white"
          }`}
        >
          Checkout
        </button>
      </div>
    </div>
  );
});

export default ItemsDetailsFormComponent;
